import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Section, Empty } from "@/components/kit";
import { useAuth, isAdmin } from "@/context/AuthContext";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate } from "@/lib/api";
import Account from "@/pages/Account";

const CHECKIN_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function FormBuilder() {
  const [form] = useLoad("/checkin-form");
  const [fields, setFields] = useState([]);
  const [dueDay, setDueDay] = useState(6);
  useEffect(() => { if (form && !Array.isArray(form)) { setFields(form.fields); setDueDay(form.due_day ?? 6); } }, [form]);
  const upd = (i, k, v) => setFields(fields.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const save = async () => {
    try { await api.put("/checkin-form", { fields, due_day: dueDay }); toast.success("Check-in form saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title="Check-in form" testid="checkin-form-builder">
      <div className="mb-5 max-w-xs space-y-1">
        <Label htmlFor="checkin-due-day">Check-in due day</Label>
        <Select value={String(dueDay)} onValueChange={(day) => setDueDay(Number(day))}>
          <SelectTrigger id="checkin-due-day" data-testid="checkin-due-day-select"><SelectValue /></SelectTrigger>
          <SelectContent>{CHECKIN_DAYS.map((day, index) => <SelectItem key={day} value={String(index)}>{day}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {fields.map((f, i) => (
        <div key={i} className="grid grid-cols-[1fr_1.5fr_140px_auto_auto] gap-2 mb-2 items-center" data-testid={`form-field-${i}`}>
          <Input value={f.key} onChange={(e) => upd(i, "key", e.target.value.replace(/\s+/g, "_").toLowerCase())} placeholder="key" className="font-mono-k text-xs" />
          <Input value={f.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label" data-testid={`form-field-label-${i}`} />
          <Select value={f.type} onValueChange={(v) => upd(i, "type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["number", "scale", "text", "boolean"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-slate-400"><Switch checked={!!f.required} onCheckedChange={(v) => upd(i, "required", v)} />req</label>
          <Button variant="ghost" size="icon" onClick={() => setFields(fields.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
        </div>
      ))}
      <div className="flex gap-2 mt-4">
        <Button variant="outline" onClick={() => setFields([...fields, { key: `field_${fields.length + 1}`, label: "", type: "text", required: false }])} data-testid="form-add-field-btn"><Plus size={14} className="mr-1" />Field</Button>
        <Button onClick={save} data-testid="form-save-btn">Save form</Button>
      </div>
    </Section>
  );
}

const EMPTY_HABIT = {
  name: "", target: "daily", kind: "quantity", target_amount: "", unit: "", increment_amount: "",
  frequency: "", days_of_week: [], timing_mode: "", scheduled_time: "", reminder_times: [],
};

const HABIT_DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const HABIT_UNITS = ["ml", "L", "min", "sec", "km", "m", "steps", "glasses", "drinks", "times"];
const REMOVED_HABIT_UNITS = ["mt", "reps", "kg", "lb", "feet"];

function HabitDefinitionFields({ value, onChange, prefix }) {
  const update = (key, next) => onChange({ ...value, [key]: next });
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-name`}>Habit name <span className="text-red-500" aria-hidden="true">*</span></Label>
          <Input id={`${prefix}-name`} required value={value.name} onChange={(event) => update("name", event.target.value)} placeholder="e.g. Drink water" data-testid={`${prefix}-name`} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-frequency`}>Frequency <span className="text-red-500" aria-hidden="true">*</span></Label>
          <Select required value={value.frequency || undefined} onValueChange={(frequency) => update("frequency", frequency)}>
            <SelectTrigger id={`${prefix}-frequency`} aria-label="Habit frequency" aria-required="true" data-testid={`${prefix}-frequency`}><SelectValue placeholder="Choose frequency" /></SelectTrigger>
            <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="specific_days">Specific days</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-timing-mode`}>When can this habit be done? <span className="text-red-500" aria-hidden="true">*</span></Label>
          <Select required value={value.timing_mode || undefined} onValueChange={(timingMode) => update("timing_mode", timingMode)}>
            <SelectTrigger id={`${prefix}-timing-mode`} aria-label="Habit timing" aria-required="true" data-testid={`${prefix}-timing-mode`}><SelectValue placeholder="Choose timing" /></SelectTrigger>
            <SelectContent><SelectItem value="anytime">Any time</SelectItem><SelectItem value="specific_time">Specific time</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-target-amount`}>Daily goal</Label>
          <div className="flex h-9 w-full min-w-0 overflow-hidden rounded-md border border-input bg-[#111827] focus-within:border-[#FF3B30] focus-within:ring-1 focus-within:ring-[#FF3B30]">
            <Input id={`${prefix}-target-amount`} type="number" min="0" step="any" value={value.target_amount} onChange={(event) => update("target_amount", event.target.value)} placeholder="e.g. 3000" aria-label="Daily goal amount" className="h-9 min-w-0 flex-1 rounded-none border-0 bg-transparent px-3 shadow-none focus-visible:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-testid={`${prefix}-target-amount`} />
            <select id={`${prefix}-unit`} required={value.target_amount !== ""} value={value.unit} onChange={(event) => update("unit", event.target.value)} aria-label="Daily goal unit" aria-required={value.target_amount !== ""} className="h-9 w-20 shrink-0 cursor-pointer appearance-none bg-transparent px-2 text-center text-xs text-slate-300 focus:outline-none [&>option]:bg-[#0B1220]" data-testid={`${prefix}-unit`}>
              <option value="" disabled>Unit</option>
              {value.unit && !HABIT_UNITS.includes(value.unit) && !REMOVED_HABIT_UNITS.includes(value.unit) && <option value={value.unit}>{value.unit}</option>}
              {HABIT_UNITS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
            </select>
            {value.target_amount !== "" && <span className="pr-2 text-xs text-red-500" aria-hidden="true">*</span>}
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-increment`}>Quick-add amount</Label>
          <Input id={`${prefix}-increment`} type="number" min="0" step="any" value={value.increment_amount} onChange={(event) => update("increment_amount", event.target.value)} placeholder="e.g. 200" data-testid={`${prefix}-increment`} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${prefix}-reminder-times`}>Reminder times</Label>
          <Input id={`${prefix}-reminder-times`} value={value.reminder_times.join(", ")} onChange={(event) => update("reminder_times", event.target.value.split(",").map((time) => time.trim()).filter(Boolean))} placeholder="HH:MM, comma separated" data-testid={`${prefix}-reminder-times`} />
        </div>
      </div>
      {value.timing_mode === "specific_time" && (
        <div className="grid gap-2 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor={`${prefix}-scheduled-time`}>Specific time <span className="text-red-500" aria-hidden="true">*</span></Label>
            <Input id={`${prefix}-scheduled-time`} type="time" required value={value.scheduled_time} onChange={(event) => update("scheduled_time", event.target.value)} data-testid={`${prefix}-scheduled-time`} />
          </div>
        </div>
      )}
      {value.frequency === "specific_days" && (
        <div className="space-y-1">
          <Label>Days of week <span className="text-red-500" aria-hidden="true">*</span></Label>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Choose days of week" data-testid={`${prefix}-days-of-week`}>
            {HABIT_DAYS.map(([label, day]) => {
              const selected = value.days_of_week.includes(day);
              return <Button key={day} type="button" size="sm" variant={selected ? "default" : "outline"} aria-pressed={selected}
                onClick={() => update("days_of_week", selected ? value.days_of_week.filter((valueDay) => valueDay !== day) : [...value.days_of_week, day].sort())}>{label}</Button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function habitFormValue(habit) {
  return {
    ...EMPTY_HABIT,
    ...habit,
    kind: "quantity",
    unit: REMOVED_HABIT_UNITS.includes(habit.unit) ? "" : habit.unit || "",
    frequency: habit.frequency || "daily",
    days_of_week: habit.days_of_week || [],
    timing_mode: habit.timing_mode || (habit.scheduled_time ? "specific_time" : "anytime"),
    target_amount: habit.target_amount ?? "",
    increment_amount: habit.increment_amount ?? "",
    scheduled_time: habit.scheduled_time || "",
    reminder_times: habit.reminder_times || [],
  };
}

function HabitCatalogRow({ habit, onSaved }) {
  const [value, setValue] = useState(() => habitFormValue(habit));
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/habit-catalog/${habit.id}`, { ...value, target_amount: value.target_amount === "" ? null : Number(value.target_amount), increment_amount: value.increment_amount === "" ? null : Number(value.increment_amount) });
      toast.success("Habit updated");
      setEditing(false);
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  const validGoal = value.target_amount === "" || (Number(value.target_amount) > 0 && value.unit.trim());
  const canSave = value.name.trim() && validGoal && value.frequency && value.timing_mode && (value.timing_mode !== "specific_time" || value.scheduled_time) && (value.frequency !== "specific_days" || value.days_of_week.length > 0);
  return (
    <div className="border-t border-[#1E293B] py-3" data-testid={`habit-catalog-row-${habit.id}`}>
      {!editing ? (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{habit.name}</div>
            <div className="truncate text-xs text-slate-500">
              {habit.target_amount != null ? `${habit.target_amount} ${habit.unit} daily goal` : "No daily goal"}
              {` · ${habit.frequency === "specific_days" ? (habit.days_of_week || []).map((day) => HABIT_DAYS[day]?.[0]).filter(Boolean).join(", ") : "Daily"}`}
            </div>
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setEditing(true)} aria-label={`Edit ${habit.name}`} title={`Edit ${habit.name}`} data-testid={`habit-catalog-edit-${habit.id}`}>
            <Pencil size={14} />
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1"><HabitDefinitionFields value={value} onChange={setValue} prefix={`habit-catalog-${habit.id}`} /></div>
          <div className="flex shrink-0 gap-2">
            <Button type="button" variant="outline" onClick={save} disabled={saving || !canSave} data-testid={`habit-catalog-save-${habit.id}`}>{saving ? "Saving…" : "Save"}</Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => { setValue(habitFormValue(habit)); setEditing(false); }} aria-label="Cancel editing habit" title="Cancel" data-testid={`habit-catalog-cancel-${habit.id}`}><X size={14} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function HabitCatalog() {
  const [catalog, reload] = useLoad("/habit-catalog");
  const [value, setValue] = useState(EMPTY_HABIT);
  const [saving, setSaving] = useState(false);
  const add = async () => {
    setSaving(true);
    try {
      await api.post("/habit-catalog", { ...value, target_amount: value.target_amount === "" ? null : Number(value.target_amount), increment_amount: value.increment_amount === "" ? null : Number(value.increment_amount) });
      setValue(EMPTY_HABIT);
      reload();
      toast.success("Habit added");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  const validGoal = value.target_amount === "" || (Number(value.target_amount) > 0 && value.unit.trim());
  const canAdd = value.name.trim() && validGoal && value.frequency && value.timing_mode && (value.timing_mode !== "specific_time" || value.scheduled_time) && (value.frequency !== "specific_days" || value.days_of_week.length > 0);
  return (
    <Section title="Habit library" testid="habit-catalog-settings">
      <div className="mb-5 space-y-3">
        <HabitDefinitionFields value={value} onChange={setValue} prefix="habit-catalog-new" />
        <div className="flex justify-end">
          <Button type="button" onClick={add} disabled={saving || !canAdd} data-testid="habit-catalog-add-btn"><Plus size={14} className="mr-1" />Add habit</Button>
        </div>
      </div>
      {!catalog ? <div className="eyebrow">Loading…</div> : !catalog.length ? <Empty text="No habits configured." /> : catalog.map((habit) => <HabitCatalogRow key={habit.id} habit={habit} onSaved={reload} />)}
    </Section>
  );
}

function Coaches() {
  const [list, reload] = useLoad("/admin/coaches");
  const toggle = async (c) => { try { await api.put(`/admin/coaches/${c.id}`, { active: c.kfh_blocked }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section title="Coaches (from your CRM)" testid="coaches-section">
      <p className="text-xs text-slate-500 mb-4">Coaches sign in with their CRM (Supabase) email and password. Roles come from <span className="font-mono-k">user_roles</span>. To add a coach, invite them in your CRM and give them the coach role. The switch blocks K-FitHUB access only.</p>
      {!list ? <div className="eyebrow">Loading…</div> : list.map((c) => (
        <div key={c.id} className="flex justify-between items-center py-2.5 border-t border-[#1E293B] text-sm" data-testid={`coach-row-${c.id}`}>
          <div>{c.name} <span className="text-slate-500 text-xs ml-2">{c.email} · {c.client_count} members</span>
            <div className="text-[11px] mt-0.5">
              {c.crm_active === false && <span className="text-red-400 mr-2">Inactive in CRM</span>}
              {c.last_sign_in_at ? <span className="text-emerald-400">Last sign-in {fmtDate(c.last_sign_in_at)}</span>
                : <span className="text-amber-400">{c.has_login ? "Has a login - not signed in yet" : "No login yet"}</span>}
            </div>
          </div>
          <Switch checked={!c.kfh_blocked} onCheckedChange={() => toggle(c)} data-testid={`coach-active-${c.id}`} />
        </div>
      ))}
    </Section>
  );
}

function Audit() {
  const [list] = useLoad("/admin/audit");
  return (
    <Section title="Audit log (sensitive actions)" testid="audit-section">
      {!list?.length ? <Empty text="No events yet." /> : list.map((a) => (
        <div key={a.id} className="grid grid-cols-[150px_1fr_1fr] gap-3 py-2 border-t border-[#1E293B] text-xs">
          <span className="text-slate-500 font-mono-k">{fmtDate(a.at)} {new Date(a.at).toLocaleTimeString()}</span>
          <span className="text-slate-300">{a.action.replace(/_/g, " ")}</span>
          <span className="text-slate-500 truncate">{a.user_email} → {a.entity} {a.entity_id.slice(0, 8)}</span>
        </div>
      ))}
    </Section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  return (
    <div>
      <PageHeader eyebrow="Configuration" title="Settings" testid="settings-title" />
      <Tabs defaultValue="checkin">
        <TabsList className="bg-[#111827] border border-[#1E293B] mb-6">
          <TabsTrigger value="checkin" data-testid="settings-tab-checkin">Check-in form</TabsTrigger>
          <TabsTrigger value="habits" data-testid="settings-tab-habits">Habits</TabsTrigger>
          <TabsTrigger value="account" data-testid="settings-tab-account">Account</TabsTrigger>
          {admin && <TabsTrigger value="coaches" data-testid="settings-tab-coaches">Coaches</TabsTrigger>}
          {admin && <TabsTrigger value="audit" data-testid="settings-tab-audit">Audit log</TabsTrigger>}
        </TabsList>
        <TabsContent value="checkin"><FormBuilder /></TabsContent>
        <TabsContent value="habits"><HabitCatalog /></TabsContent>
        <TabsContent value="account"><Account /></TabsContent>
        {admin && <TabsContent value="coaches"><Coaches /></TabsContent>}
        {admin && <TabsContent value="audit"><Audit /></TabsContent>}
      </Tabs>
    </div>
  );
}
